// /* eslint jsx-a11y/no-autofocus: 0 */
import moment from 'moment';
import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
// import PropTypes from 'prop-types'
import type { Moment } from 'moment';
import Trigger from 'rc-trigger';
import type { KeyboardEventHandler, MouseEvent, MouseEventHandler, ReactElement } from 'react';
import { getNid } from '../../../wui-core/src';
import Input from './Input';
import type { AmpmType, RCTimePickerProps } from './iRCTimePicker';
import Panel from './Panel';
import placements from './placements';

// function noop() {}

// 增加的计算时间项的方法
function generateOptions(length: number, disabledOptions: number[], hideDisabledOptions: boolean, step: number = 1) {
    // 生成选项数组
    const arr: number[] = []
    for (let value = 0; value < length; value += step) {
        if (!disabledOptions || disabledOptions.indexOf(value) < 0 || !hideDisabledOptions) {
            arr.push(value)
        }
    }
    return arr
}

function toNearestValidTime(time: Moment, hourOptions: number[], minuteOptions: number[], secondOptions: number[]) {
    // 至最近的合法时间
    const hour = hourOptions.slice().sort((a: number, b: number) => Math.abs(time.hour() - a) - Math.abs(time.hour() - b))[0]
    const minute = minuteOptions.slice().sort((a: number, b: number) => Math.abs(time.minute() - a) - Math.abs(time.minute() - b))[0]
    const second = secondOptions.slice().sort((a: number, b: number) => Math.abs(time.second() - a) - Math.abs(time.second() - b))[0]
    return moment(`${hour}:${minute}:${second}`, 'HH:mm:ss')
}

const defaultProps = {
    clearText: 'clear',
    prefixCls: 'rc-time-picker',
    defaultOpen: false,
    inputReadOnly: false,
    className: '',
    popupClassName: '',
    id: '',
    defaultOpenValue: moment(),
    allowEmpty: true,
    showHour: true,
    showMinute: true,
    showSecond: true,
    hideDisabledOptions: false,
    placement: 'bottomLeft',
    renderExtraFooter: () => null,
    use12Hours: false,
    focusOnOpen: false,
    allowClear: true,
    inputWidth: 0
}

const Picker = forwardRef<any, RCTimePickerProps>((props, ref) => {
    const mergedProps = { ...defaultProps, ...props }
    const {
        defaultOpen,
        defaultValue,
        open: propsOpen = defaultOpen,
        value: propsValue = defaultValue,
        autoFocus,
        use12Hours,
        disabledHours = () => ([]),
        disabledMinutes = () => ([]),
        disabledSeconds = () => ([]),
        hideDisabledOptions,
        hourStep,
        minuteStep,
        secondStep,
        defaultOpenValue,
        showHour,
        showMinute,
        showSecond,
        format,
        prefixCls,
        fieldid,
        locale,
        clearText,
        onChange,
        onAmPmChange,
        onOpen,
        onClose,
        onOpenChange,
        onFocus,
        onBlur,
        onKeyDown,
        onEsc,
        clearIcon,
        allowEmpty,
        placeholder,
        placement,
        align,
        disabled,
        transitionName,
        style,
        className,
        getPopupContainer,
        name,
        autoComplete,
        inputReadOnly,
        inputIcon,
        popupStyle,
        allowClear,
        focusOnOpen,
        renderExtraFooter,
        dir,
        popupClassName
    } = mergedProps

    const pickerRef = useRef<HTMLSpanElement>(null)
    const inputRef = useRef<HTMLInputElement>(null)
    const panelRef = useRef<any>(null)

    const [inputWidth, setInputWidth] = useState<number | undefined>(undefined)
    const [inputFocused, setInputFocused] = useState(!!autoFocus)
    const [panelFocused, setPanelFocused] = useState(propsOpen ?? defaultOpen ?? false)
    const [open, setOpen] = useState(propsOpen ?? defaultOpen ?? false)
    const [value, setValue] = useState<Moment | null | undefined>(propsValue ?? defaultValue)

    // 处理 value 变化
    useEffect(() => {
        if ('value' in props) {
            setValue(propsValue);
        }
    }, [propsValue, props])

    // 处理 open 变化
    useEffect(() => {
        if (propsOpen !== undefined) {
            setOpen(propsOpen);
            setPanelFocused(propsOpen);
        }
    }, [propsOpen])

    const onPanelChange = useCallback((value: Moment | null) => {
        if (!('value' in props)) {
            setValue(value)
        }
        onChange?.(value)
    }, [props, onChange])

    const onPanelClick = useCallback(() => {
        setPanelFocused(true)
    }, [])

    const onInputFocus = useCallback((e: React.FocusEvent<Element, Element>) => {
        if (inputReadOnly) return
        setInputFocused(true)
        onFocus?.(e)
    }, [onFocus])

    const onInputBlur = useCallback((value: Moment) => {
        onPanelChange(value)
        setInputFocused(false)
        onBlur?.(value)
    }, [onPanelChange, onBlur])

    const blurHandle = useCallback(() => {
        setInputFocused(false)
    }, [])

    const onAmPmChangeHandler = useCallback((ampm: AmpmType) => {
        onAmPmChange?.(ampm)
    }, [onAmPmChange])

    const handleOpenChange = useCallback((open: boolean) => {
        setOpen(open)
        setPanelFocused(false)
    }, [])

    const onClear: MouseEventHandler = useCallback((event) => {
        event.stopPropagation()
        onPanelChange(null)
        setOpen(false)
        setPanelFocused(false)
    }, [onPanelChange])

    const onVisibleChange = useCallback((open: boolean) => {
        if (!('open' in props)) {
            setOpen(open)
        }
        setPanelFocused(open)
        onOpenChange?.({open})
        if (open) {
            onOpen?.({open})
        } else {
            onClose?.({open})
        }
    }, [props, onOpenChange, onOpen, onClose])

    const onKeyDownHandler: KeyboardEventHandler = useCallback((e) => {
        if (e.key === 'ArrowDown' || e.keyCode === 40) {
            setOpen(true)
        }
        onKeyDown?.(e)
    }, [onKeyDown])

    const getFormat = useCallback(() => {
        if (format) {
            return format
        }

        if (use12Hours) {
            const fmtString = [showHour ? 'h' : '', showMinute ? 'mm' : '', showSecond ? 'ss' : '']
                .filter(item => !!item)
                .join(':')

            return fmtString.concat(' A')
        }

        return [showHour ? 'HH' : '', showMinute ? 'mm' : '', showSecond ? 'ss' : ''].filter(item => !!item).join(':')
    }, [format, use12Hours, showHour, showMinute, showSecond])

    const isAM = useCallback(() => {
        const realValue = value || defaultOpenValue;
        return realValue && realValue.hour() >= 0 && realValue.hour() < 12;
    }, [value, defaultOpenValue])

    // 计算不可用小时选项
    const disabledHoursHandler = useCallback(() => {
        let disabledOptions = disabledHours()
        if (use12Hours && Array.isArray(disabledOptions)) {
            if (isAM()) {
                disabledOptions = disabledOptions.filter(h => h < 12).map(h => (h === 0 ? 12 : h))
            } else {
                disabledOptions = disabledOptions.map(h => (h === 12 ? 12 : h - 12))
            }
        }
        return disabledOptions
    }, [use12Hours, disabledHours, isAM])

    // 处理输入框宽度变化
    const handleInputWidthChange = useCallback((width: number) => {
        setInputWidth(width)
    }, [])

    const getPanelElement = useCallback(() => {
        return (
            <Panel
                clearText={clearText}
                prefixCls={`${prefixCls}-panel`}
                ref={panelRef}
                fieldid={fieldid}
                locale={locale}
                value={value}
                dir={dir}
                onClick={onPanelClick}
                onChange={onPanelChange}
                onAmPmChange={onAmPmChangeHandler}
                onOpenChange={handleOpenChange}
                defaultOpenValue={defaultOpenValue}
                showHour={showHour!}
                showMinute={showMinute!}
                showSecond={showSecond!}
                format={getFormat()}
                disabledHours={disabledHours}
                disabledMinutes={disabledMinutes}
                disabledSeconds={disabledSeconds}
                hideDisabledOptions={hideDisabledOptions!}
                use12Hours={use12Hours}
                hourStep={hourStep!}
                minuteStep={minuteStep!}
                secondStep={secondStep!}
                renderExtraFooter={renderExtraFooter}
                width={inputWidth}
            />
        )
    }, [
        clearText, prefixCls, fieldid, locale, value, dir, onPanelClick, onPanelChange,
        onAmPmChangeHandler, handleOpenChange, defaultOpenValue, showHour, showMinute,
        showSecond, getFormat, disabledHours, disabledMinutes, disabledSeconds,
        hideDisabledOptions, use12Hours, hourStep, minuteStep, secondStep,
        renderExtraFooter, inputWidth
    ])

    const getPopupClassName = useCallback(() => {
        let className = popupClassName
        // Keep it for old compatibility
        if ((!showHour || !showMinute || !showSecond) && !use12Hours) {
            className += ` ${prefixCls}-panel-narrow`
        }
        let selectColumnCount = 0
        if (showHour) {
            selectColumnCount += 1
        }
        if (showMinute) {
            selectColumnCount += 1
        }
        if (showSecond) {
            selectColumnCount += 1
        }
        if (use12Hours) {
            selectColumnCount += 1
        }
        className += ` ${prefixCls}-panel-column-${selectColumnCount}`
        return className
    }, [showHour, showMinute, showSecond, use12Hours, prefixCls, popupClassName])

    const renderClearButton = useCallback(() => {
        if (!allowEmpty || !value) {
            return null
        }

        if (React.isValidElement(clearIcon)) {
            return React.cloneElement(clearIcon, {
                fieldid: fieldid ? fieldid + '_clear' : undefined,
                title: clearText,
                onClick: (e: MouseEvent<HTMLElement>) => {
                    if ((clearIcon as ReactElement).props?.onClick) (clearIcon as ReactElement).props?.onClick(e)
                    onClear(e)
                }
            })
        }

        return (
            <a
                role='button'
                className={`${prefixCls}-clear`}
                title={clearText}
                onClick={onClear}
                tabIndex={0}
                fieldid={fieldid ? fieldid + '_clear' : undefined}
            >
                {clearIcon || <i className={`${prefixCls}-clear-icon`} />}
            </a>
        )
    }, [allowEmpty, value, clearIcon, fieldid, clearText, prefixCls, onClear])

    // 将之前Header里的处理逻辑放到TimePicker中
    const disabledHourOptions = useMemo(() => disabledHoursHandler(), [disabledHoursHandler])
    const disabledMinuteOptions = useMemo(() => disabledMinutes(value ? value.hour() : null), [disabledMinutes, value])
    const disabledSecondOptions = useMemo(() =>
        disabledSeconds(value ? value.hour() : null, value ? value.minute() : null),
    [disabledSeconds, value]
    )
    const hourOptions = useMemo(() =>
        generateOptions(24, disabledHourOptions, hideDisabledOptions!, hourStep),
    [disabledHourOptions, hideDisabledOptions, hourStep]
    )
    const minuteOptions = useMemo(() =>
        generateOptions(60, disabledMinuteOptions, hideDisabledOptions!, minuteStep),
    [disabledMinuteOptions, hideDisabledOptions, minuteStep]
    )
    const secondOptions = useMemo(() =>
        generateOptions(60, disabledSecondOptions, hideDisabledOptions!, secondStep),
    [disabledSecondOptions, hideDisabledOptions, secondStep]
    )

    const validDefaultOpenValue = useMemo(() =>
        defaultOpenValue && toNearestValidTime(defaultOpenValue, hourOptions, minuteOptions, secondOptions),
    [defaultOpenValue, hourOptions, minuteOptions, secondOptions]
    )

    // 暴露 focus 和 blur 方法给父组件
    useImperativeHandle(ref, () => ({
        focus: () => {
            inputRef.current?.focus()
        },
        blur: () => {
            inputRef.current?.blur()
        }
    }), [])

    const focusedCls = inputFocused || panelFocused ? `${prefixCls}-focused` : ''
    let adapterNid = getNid(mergedProps)

    return (
        <Trigger
            prefixCls={`${prefixCls}-panel`}
            popupClassName={getPopupClassName()}
            popupStyle={popupStyle}
            popup={getPanelElement()}
            popupAlign={align}
            builtinPlacements={placements}
            popupPlacement={placement}
            action={disabled || inputReadOnly ? [] : ['click']}
            destroyPopupOnHide
            getPopupContainer={getPopupContainer}
            popupTransitionName={transitionName}
            popupVisible={open}
            onPopupVisibleChange={onVisibleChange}
        >
            <span ref={pickerRef} fieldid={fieldid} className={`${prefixCls} ${className} ${focusedCls}`} style={style} {...adapterNid}>
                <Input
                    ref={inputRef}
                    prefixCls={prefixCls!}
                    onFocus={onInputFocus}
                    onBlur={onInputBlur}
                    blurHandle={blurHandle}
                    format={getFormat()}
                    autoFocus={autoFocus}
                    onChange={onPanelChange}
                    autoComplete={autoComplete!}
                    disabled={disabled}
                    onKeyDown={onKeyDownHandler}
                    name={name}
                    showHour={showHour!}
                    showMinute={showMinute!}
                    showSecond={showSecond!}
                    placeholder={placeholder}
                    id={mergedProps.id}
                    fieldid={fieldid}
                    readOnly={!!inputReadOnly}
                    locale={locale}
                    value={value}
                    clearText={clearText}
                    defaultOpenValue={validDefaultOpenValue!}
                    open={!!open}
                    onEsc={onEsc}
                    hourOptions={hourOptions}
                    minuteOptions={minuteOptions}
                    secondOptions={secondOptions}
                    disabledHours={disabledHoursHandler}
                    disabledMinutes={disabledMinutes!}
                    disabledSeconds={disabledSeconds!}
                    allowEmpty={allowEmpty!}
                    focusOnOpen={focusOnOpen!}
                    handleInputWidthChange={handleInputWidthChange}
                />
                {allowClear && !disabled && !inputReadOnly && renderClearButton()}
                {inputIcon || <span className={`${prefixCls}-icon`} />}
            </span>
        </Trigger>
    )
})

Picker.displayName = 'Picker'

export default Picker